/** Weekly install-offer cadence for customer PWA when opened in a browser tab. */

export const INSTALL_OFFER_SHOWN_AT_KEY = 'customer-pwa-install-shown-at';

/**
 * Persisted install evidence for Settings → Install honesty (browser-tab paths).
 *
 * Detection limits (honest — do not overclaim):
 * - `display-mode: standalone` / iOS `navigator.standalone` only prove the *current*
 *   browsing context is the installed UI — a Safari tab after A2HS is still a tab.
 * - iOS Safari has no `beforeinstallprompt` and no `getInstalledRelatedApps`; Home
 *   Screen PWAs share origin localStorage with the Safari tab, so a stamp written
 *   while standalone (or after Chromium `appinstalled` / BIP accept) is the only
 *   reliable cross-context signal on iOS.
 * - Chromium may expose `getInstalledRelatedApps` when the manifest declares
 *   `related_applications`; absence of related apps / stamp does **not** prove
 *   the app is uninstalled.
 */
export const CUSTOMER_PWA_INSTALL_EVIDENCE_KEY = 'customer-pwa-install-evidence';

/** Legacy key from the soft-snooze experiment — still honored if present. */
const LEGACY_INSTALL_OFFER_DISMISSED_AT_KEY = 'customer-pwa-install-dismissed-at';

/** Show the install offer at most once per this interval (7 days). */
export const INSTALL_OFFER_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

type NavigatorWithInstalledRelatedApps = Navigator & {
  getInstalledRelatedApps?: () => Promise<ReadonlyArray<{ id?: string }>>;
};

export function isCustomerPwaInstalled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const standaloneDisplay =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone =
    'standalone' in navigator &&
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standaloneDisplay || iosStandalone;
}

/** Persist positive install evidence (standalone / appinstalled / BIP accepted). */
export function markCustomerPwaInstallEvidence(
  storage: Pick<Storage, 'setItem'> = window.localStorage,
): void {
  storage.setItem(CUSTOMER_PWA_INSTALL_EVIDENCE_KEY, '1');
}

export function hasCustomerPwaInstallEvidence(
  storage: Pick<Storage, 'getItem'> = window.localStorage,
): boolean {
  return storage.getItem(CUSTOMER_PWA_INSTALL_EVIDENCE_KEY) === '1';
}

export function clearCustomerPwaInstallEvidence(
  storage: Pick<Storage, 'removeItem'> = window.localStorage,
): void {
  storage.removeItem(CUSTOMER_PWA_INSTALL_EVIDENCE_KEY);
}

/** Stamp localStorage when the current context is already the installed PWA. */
export function stampCustomerPwaInstallEvidenceIfStandalone(
  storage: Pick<Storage, 'setItem'> = window.localStorage,
): void {
  if (isCustomerPwaInstalled()) {
    markCustomerPwaInstallEvidence(storage);
  }
}

async function hasInstalledRelatedAppsEvidence(): Promise<boolean> {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const getInstalledRelatedApps = (
    navigator as NavigatorWithInstalledRelatedApps
  ).getInstalledRelatedApps;
  if (typeof getInstalledRelatedApps !== 'function') {
    return false;
  }
  try {
    const related = await getInstalledRelatedApps.call(navigator);
    return Array.isArray(related) && related.length > 0;
  } catch {
    return false;
  }
}

/**
 * Positive already-installed evidence for Settings Install (not a BIP event).
 * Prefers `getInstalledRelatedApps` when supported; also honors standalone +
 * the persisted evidence flag (iOS Safari ↔ Home Screen share localStorage).
 */
export async function hasPositiveCustomerPwaInstallEvidence(
  storage: Pick<Storage, 'getItem' | 'setItem'> = window.localStorage,
): Promise<boolean> {
  if (isCustomerPwaInstalled()) {
    markCustomerPwaInstallEvidence(storage);
    return true;
  }
  if (await hasInstalledRelatedAppsEvidence()) {
    markCustomerPwaInstallEvidence(storage);
    return true;
  }
  return hasCustomerPwaInstallEvidence(storage);
}

export function readInstallOfferShownAt(
  storage: Pick<Storage, 'getItem'> = window.localStorage,
): number | null {
  const raw =
    storage.getItem(INSTALL_OFFER_SHOWN_AT_KEY) ??
    storage.getItem(LEGACY_INSTALL_OFFER_DISMISSED_AT_KEY);
  if (raw === null || raw === '') {
    return null;
  }
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

/** True when the site is in a normal browser tab and the weekly offer window is open. */
export function isInstallOfferDue(
  nowMs: number = Date.now(),
  storage: Pick<Storage, 'getItem'> = window.localStorage,
): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  if (isCustomerPwaInstalled()) {
    return false;
  }
  const shownAt = readInstallOfferShownAt(storage);
  if (shownAt === null) {
    return true;
  }
  return nowMs - shownAt >= INSTALL_OFFER_COOLDOWN_MS;
}

/** Record that the offer was displayed so it will not show again until the cooldown elapses. */
export function markInstallOfferShown(
  nowMs: number = Date.now(),
  storage: Pick<Storage, 'setItem' | 'removeItem'> = window.localStorage,
): void {
  storage.setItem(INSTALL_OFFER_SHOWN_AT_KEY, String(nowMs));
  storage.removeItem(LEGACY_INSTALL_OFFER_DISMISSED_AT_KEY);
}

export function clearInstallOfferShown(
  storage: Pick<Storage, 'removeItem'> = window.localStorage,
): void {
  storage.removeItem(INSTALL_OFFER_SHOWN_AT_KEY);
  storage.removeItem(LEGACY_INSTALL_OFFER_DISMISSED_AT_KEY);
}
